/**
 * Cookie-consent state management. AVG + Google Consent Mode v2-compatibel.
 *
 * Categorieën:
 *   - necessary: altijd aan (functioneel — form, brochure-token, security)
 *   - analytics: GA4 / GTM analyse-tags
 *   - marketing: Meta Pixel, Google Ads, retargeting
 *
 * Opslag: localStorage (client-side). Keuze blijft 12 maanden geldig.
 *
 * Consent Mode v2 mapping:
 *   - analytics_storage           ← analytics
 *   - ad_storage                  ← marketing
 *   - ad_user_data                ← marketing
 *   - ad_personalization          ← marketing
 *   - functionality_storage       ← necessary (altijd granted)
 *   - personalization_storage     ← analytics
 *   - security_storage            ← necessary (altijd granted)
 */

export interface ConsentState {
  necessary: true; // altijd true
  analytics: boolean;
  marketing: boolean;
  /** ISO-date van de keuze */
  decided_at: string;
  /** Versie — bump deze als je categorieën verandert om user opnieuw te vragen */
  version: number;
}

export const CONSENT_VERSION = 1;
const LS_KEY = "bw_consent_v1";
const COOKIE_NAME = "bw_consent";
const ONE_YEAR = 60 * 60 * 24 * 365;

export const CONSENT_ALL_GRANTED: ConsentState = {
  necessary: true,
  analytics: true,
  marketing: true,
  decided_at: new Date().toISOString(),
  version: CONSENT_VERSION,
};

export const CONSENT_ONLY_NECESSARY: ConsentState = {
  necessary: true,
  analytics: false,
  marketing: false,
  decided_at: new Date().toISOString(),
  version: CONSENT_VERSION,
};

/** Lees consent uit localStorage. null = nog geen keuze. */
export function readConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== CONSENT_VERSION) return null; // verouderd → opnieuw vragen
    return parsed;
  } catch {
    return null;
  }
}

/** Schrijf consent weg + push update naar gtag/dataLayer. */
export function writeConsent(state: ConsentState): void {
  if (typeof window === "undefined") return;
  const withTime = { ...state, decided_at: new Date().toISOString(), version: CONSENT_VERSION };
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(withTime));
    // Dup naar cookie voor SSR-detectie (server kan 'm lezen voor GTM-conditional)
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(
      JSON.stringify({ a: state.analytics ? 1 : 0, m: state.marketing ? 1 : 0, v: CONSENT_VERSION })
    )};path=/;max-age=${ONE_YEAR};SameSite=Lax`;
  } catch {
    /* ignore */
  }
  pushConsentUpdate(state);
}

/** Push Google Consent Mode v2 update naar dataLayer + Clarity. */
export function pushConsentUpdate(state: ConsentState): void {
  if (typeof window === "undefined") return;
  type DLWin = typeof window & {
    dataLayer?: unknown[];
    clarity?: (...args: unknown[]) => void;
  };
  const w = window as DLWin;
  w.dataLayer = w.dataLayer || [];
  // gtag('consent', 'update', {...}) wordt als array gepusht
  w.dataLayer.push({
    event: "consent_update",
    ...consentModeSignals(state),
  });
  // Ook een aparte 'gtag consent update' format (legacy arguments-style):
  w.dataLayer.push([
    "consent",
    "update",
    consentModeSignals(state),
  ]);
  // Microsoft Clarity heeft een EIGEN consent API (los van gtag). Zonder
  // deze call worden sessies NIET opgenomen, ook al staat analytics_storage
  // op 'granted' in gtag. clarity('consent', true) zet de Clarity-side
  // consent op accepted. Defaults zonder call = denied.
  try {
    if (typeof w.clarity === "function") {
      w.clarity("consent", state.analytics);
    }
  } catch {
    /* clarity niet geladen of error — geen impact op rest */
  }
}

/** Map ConsentState naar Google Consent Mode v2 signals. */
export function consentModeSignals(state: ConsentState): Record<string, "granted" | "denied"> {
  const analytics: "granted" | "denied" = state.analytics ? "granted" : "denied";
  const marketing: "granted" | "denied" = state.marketing ? "granted" : "denied";
  return {
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    analytics_storage: analytics,
    personalization_storage: analytics,
    functionality_storage: "granted",
    security_storage: "granted",
  };
}

/** Default-consent script string voor gtag voordat GTM laadt. */
export function defaultConsentScript(): string {
  return `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      personalization_storage: 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted',
      wait_for_update: 500
    });
    // Clarity async-queue stub — zodat clarity('consent', ...) calls die
    // VÓÓR het Clarity-script geladen wordt (lazyOnload na window.load)
    // alvast in een queue komen en automatisch worden afgespeeld zodra
    // het echte script er is. Zonder deze stub gaan early calls verloren.
    window.clarity = window.clarity || function(){(window.clarity.q=window.clarity.q||[]).push(arguments)};
    // If user already made a choice, apply it now
    try {
      var raw = localStorage.getItem('${LS_KEY}');
      if (raw) {
        var s = JSON.parse(raw);
        if (s && s.version === ${CONSENT_VERSION}) {
          gtag('consent', 'update', {
            ad_storage: s.marketing ? 'granted' : 'denied',
            ad_user_data: s.marketing ? 'granted' : 'denied',
            ad_personalization: s.marketing ? 'granted' : 'denied',
            analytics_storage: s.analytics ? 'granted' : 'denied',
            personalization_storage: s.analytics ? 'granted' : 'denied'
          });
          // Clarity consent ook updaten voor terugkerende bezoekers —
          // anders blijft Clarity in default-denied mode → 0 sessies
          // ondanks gtag-granted. Microsoft Clarity heeft eigen consent API.
          window.clarity('consent', !!s.analytics);
        }
      }
    } catch (e) {}
  `;
}

/** Trigger open van preferences-dialog (via custom event). */
export function openConsentPreferences(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("bw:consent:open"));
}
